import { AdvancedSelect, SelectionValue } from '../../widgets/select';
import { Dialog, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { Input } from '../../widgets/input';
import { PrimaryButton } from '../../widgets/buttons';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import React, { FC, Suspense, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { graphql, useMutation } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import styled from 'styled-components';
import config from '../../../shared/config';
import { ApiLabel, ButtonContainer, DoubleHr, InstructionItem, InstructionLabel, Note, PointedInstructionsList } from '../../../shared/connectDialogStyle';
import { Loader } from '../../../shared/docsDialog';
import { T, toGlobalID } from '../../../shared/helpers';
import { useValidation, validateRequired } from '../../../shared/validation';
import ApiKeys from '../myAccount/apiKeys';
import { Hr } from '../platformSelection/platformConnection';
import { cCloudIntegrationSalesChannelDialogQuery } from './__generated__/cCloudIntegrationSalesChannelDialogQuery.graphql';
import { cCloudIntegrationSalesChannelDialogSaveMutation } from './__generated__/cCloudIntegrationSalesChannelDialogSaveMutation.graphql';

interface CCloudApiProps {
    title?: string;
    salesChannelID?: string | undefined;
    windowDecorator?: FC<any> | undefined;
}

interface Currency {
    label: string;
    value: string;
}

const DescriptionLabel = styled.div`
    display: flex;
    margin-top: 8px;

    > label {
        color: #445a64;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
    }
`;

const cCloudInfo = graphql`
    query cCloudIntegrationSalesChannelDialogQuery {
        ecommerceSettings {
            ecommerceID
            ecommerceName
            apiKey
            apiSecret
            remoteUrl
            currencyGlobalID
            username # This is the siteId
        }
        currencies {
            id
            name
        }
    }
`;

const saveCCloud = graphql`
    mutation cCloudIntegrationSalesChannelDialogSaveMutation($input: EcommerceSettingsInput!) {
        saveEcommerceSettings(settingsInput: $input) {
            ecommerceID
        }
    }
`;

const CCloudIntegrationSalesChannelDialog: FC<CCloudApiProps> = (ccloud) => {
    const query = useLazyLoadQuery<cCloudIntegrationSalesChannelDialogQuery>(cCloudInfo, {});
    const [apiKey, setApiKey] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.apiKey ?? '') : '');
    const [apiSecret, setApiSecret] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.apiSecret ?? '') : '');
    const [remoteUrl, setRemoteUrl] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.remoteUrl ?? '') : '');
    const [siteId, setSiteId] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.username ?? '') : '');
    const [currency, setCurrency] = useState(query.ecommerceSettings?.ecommerceName === 'C_CLOUD' ? (query.ecommerceSettings?.currencyGlobalID ?? '') : '');
    const [recaptchaToken, setRecaptchaToken] = useState('');

    const currenciesOptions =
        query.currencies?.map((currency) => ({
            value: currency!.id,
            label: currency!.name ?? ''
        })) ?? [];

    const { closeDialog } = useDialogManager();
    const isForEdit = ccloud.salesChannelID !== undefined;

    const currencyValidation = useValidation([
        {
            key: 'currency',
            validate: validateRequired,
            message: T._('This field is required.', 'Admin')
        }
    ]);

    currencyValidation.check(currency);

    const [save, isSaving] = useMutation<cCloudIntegrationSalesChannelDialogSaveMutation>(saveCCloud);

    const handleSave = async () => {
        if (apiKey == '') {
            showErrorNotification({ message: T._('Please insert CCloud client ID', 'Admin') });
            return;
        }

        if (apiSecret == '') {
            showErrorNotification({ message: T._('Please insert CCloud secret key', 'Admin') });
            return;
        }

        if (siteId == '') {
            showErrorNotification({ message: T._('Please insert CCloud site key', 'Admin') });
            return;
        }

        if (remoteUrl == '') {
            showErrorNotification({ message: T._('Please insert CCloud site URL', 'Admin') });
            return;
        }

        if (currency == '') {
            showErrorNotification({ message: T._('Please select currency', 'Admin') });
            return;
        }

        const loadingNotification = showInfoNotification({
            message: T._('Saving...', 'Admin')
        });

        if (!isSaving) {
            save({
                variables: {
                    input: {
                        ecommerceGlobalID: toGlobalID(11, 'ECommerce'),
                        salesChannelID: toGlobalID(1, 'SalesChannel'),
                        recaptchaToken,
                        apiKey,
                        apiSecret,
                        remoteUrl,
                        siteId,
                        currencyGlobalID: currency
                    }
                },
                onCompleted: () => {
                    removeNotification(loadingNotification);
                    showSuccessNotification({
                        message: T._('Saved', 'Admin')
                    });

                    closeDialog('sales-force');
                },
                onError: () => {
                    removeNotification(loadingNotification);
                    showErrorNotification({
                        message: T._('An error occurred while saving.', 'Admin')
                    });
                }
            });
        }
    };

    const handleSelectedCurrency = (item: SelectionValue<Currency>) => {
        if (item != null && !Array.isArray(item)) {
            setCurrency(item.value);
        }
    };

    return (
        <Dialog windowDecorator={ccloud.windowDecorator} onClose={() => closeDialog('sales-force')} title={ccloud?.title ?? ''}>
            <Suspense fallback={Loader}>
                <div>
                    {!isForEdit && (
                        <>
                            <InstructionLabel dangerouslySetInnerHTML={{ __html: `<strong>PLEASE NOTE</strong>` }} />
                            <Note
                                dangerouslySetInnerHTML={{
                                    __html: `Zakeke supports integration with both SFRA and SiteGenesis. Depending on your case, please follow the dedicated guide:`
                                }}
                            />
                            <PointedInstructionsList>
                                <InstructionItem
                                    dangerouslySetInnerHTML={{
                                        __html: `<a href='https://zakeke.zendesk.com/hc/en-us/articles/360011323080-SFRA' target='_blank' rel='noopener'>SFRA</a>`
                                    }}
                                />
                                <InstructionItem
                                    dangerouslySetInnerHTML={{
                                        __html: `<a href='https://zakeke.zendesk.com/hc/en-us/articles/360011446679-Sitegenesis' target='_blank' rel='noopener'>SiteGenesis</a>`
                                    }}
                                />
                            </PointedInstructionsList>
                            <Note
                                dangerouslySetInnerHTML={{
                                    __html: `During the installation process, you will be required to fill out the form below to complete the configuration of your Zakeke account.`
                                }}
                            />
                            <Hr />
                        </>
                    )}
                    <ApiLabel dangerouslySetInnerHTML={{ __html: `<strong><h2>Integration settings</h2></strong>` }} />
                    <FormControl label={T._('CCloud client ID', 'Admin')}>
                        <Input required={true} value={apiKey} placeholder={'Insert your store URL'} onChange={(e) => setApiKey(e.currentTarget.value)} />
                    </FormControl>
                    <FormControl label={T._('CCloud secret key', 'Admin')}>
                        <Input required={true} value={apiSecret} placeholder={'Insert your store URL'} onChange={(e) => setApiSecret(e.currentTarget.value)} />
                    </FormControl>
                    <FormControl label={T._('CCloud site key', 'Admin')}>
                        <Input required={true} value={siteId} placeholder={'Insert your store URL'} onChange={(e) => setSiteId(e.currentTarget.value)} />
                    </FormControl>
                    <FormControl label={T._('CCloud site URL', 'Admin')}>
                        <Input required={true} value={remoteUrl} placeholder={'Insert your store URL'} onChange={(e) => setRemoteUrl(e.currentTarget.value)} />
                    </FormControl>
                    <FormControl label={T._('Currency', 'Admin')}>
                        <AdvancedSelect
                            placeholder={T._('US Dollar', 'Admin')}
                            isMulti={false}
                            options={currenciesOptions}
                            value={currenciesOptions.find((option) => option.value == currency)}
                            onChange={handleSelectedCurrency}
                            // width={'214px'}
                        />
                        <DescriptionLabel>
                            <label>
                                {T._(
                                    `Select your store's base currency carefully to ensure proper integration and accurate transaction fees; incorrect selection may cause issues.`,
                                    'Admin'
                                )}
                            </label>
                        </DescriptionLabel>
                    </FormControl>
                    <FormControl>
                        <ReCAPTCHA
                            sitekey={config.googleRecaptchaSitekey}
                            theme={'light'}
                            size={'normal'}
                            onChange={(e) => e && setRecaptchaToken(e)}
                        ></ReCAPTCHA>
                    </FormControl>
                    <ButtonContainer>
                        <PrimaryButton onClick={handleSave}>{T._('Save', 'Admin')}</PrimaryButton>
                    </ButtonContainer>
                    {isForEdit && (
                        <>
                            <DoubleHr />
                            <ApiLabel dangerouslySetInnerHTML={{ __html: `<strong><h2>API Keys</h2></strong>` }} />
                            <ApiKeys salesChannelID={ccloud.salesChannelID!} />
                        </>
                    )}
                </div>
            </Suspense>
        </Dialog>
    );
};

export default CCloudIntegrationSalesChannelDialog;
